import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Channel as StreamChannel } from 'stream-chat';

import { getBookingSession } from '@/features/bookings-management/api';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import type {
  BookingSession,
  BookingSessionStatus,
  BookingUser,
} from '@/features/bookings-management/types';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import { formatAddress } from '@/utils/formatAddress';
import { fDate } from '@/utils/formatTime';
import {
  coercePositiveNumber,
  getChannelMetadata,
  getRecord,
  resolveBookingSessionId,
} from '../../utils/channelOrdering';

import type { StreamChatType } from '../../types';

type JobContextHeaderProps = {
  context: JobContextHeaderData;
};

export type ChannelMetadata = Record<string, unknown>;

type ContextChipTone = 'default' | 'info' | 'success' | 'warning' | 'error';

export type ContextChip = {
  label: string;
  value: string;
  tone?: ContextChipTone;
};

export type JobContextLinks = {
  bookingPath: string;
  sessionPath?: string;
};

export type JobContextHeaderData = {
  bookingSessionId: number | null;
  chips: ContextChip[];
  isLoading: boolean;
  links: JobContextLinks | null;
  metadata: ChannelMetadata;
  session?: BookingSession;
  shouldRender: boolean;
};

const SESSION_FILTERS = ['previous', 'today', 'upcoming'];

const formatName = (user?: Partial<BookingUser>): string => {
  if (!user) return 'N/A';

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || 'N/A';
};

const formatDateValue = (value: unknown): string => {
  if (!value) return 'N/A';

  try {
    return fDate(value as string | number | Date, 'dd MMM yyyy');
  } catch {
    return String(value);
  }
};

const formatAddressValue = (session?: BookingSession): string => {
  const address = session?.address;
  if (!address) return 'N/A';

  if (address.formatted_address) {
    return formatAddress(
      address.formatted_address,
      address.postcode
    ).formattedAddressWithPostcode;
  }

  return (
    [
      address.line_1,
      address.line_2,
      address.town_or_city,
      address.postcode,
    ]
      .filter(Boolean)
      .join(', ') || 'N/A'
  );
};

const getStatusTone = (status?: BookingSessionStatus): ContextChipTone => {
  const styleClass = status?.style_class?.toLowerCase();

  if (styleClass === 'success' || styleClass === 'warning') return styleClass;
  if (styleClass === 'error') return 'error';
  if (styleClass === 'info' || styleClass === 'primary') return 'info';

  return 'default';
};

const getMetadataString = (
  metadata: ChannelMetadata,
  keys: string[]
): string => {
  for (const key of keys) {
    const value = metadata[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value);
    }
  }

  return 'N/A';
};

const getMetadataNumber = (
  metadata: ChannelMetadata,
  keys: string[]
): number | null => {
  for (const key of keys) {
    const parsed = coercePositiveNumber(metadata[key]);
    if (parsed) return parsed;
  }

  return null;
};

const getSessionFilter = (date: unknown): string => {
  if (!date || String(date) === 'N/A') return 'upcoming';

  try {
    const filter = returnSessionFilterFromDate(String(date));
    return SESSION_FILTERS.includes(filter) ? filter : 'upcoming';
  } catch {
    return 'upcoming';
  }
};

const buildJobContextLinks = (
  session: BookingSession | undefined,
  metadata: ChannelMetadata,
  bookingSessionId: number | null
): JobContextLinks | null => {
  const bookingId =
    coercePositiveNumber(session?.booking_id) ??
    getMetadataNumber(metadata, ['bookingId', 'booking_id']);

  if (!bookingId) return null;

  const sessionId = coercePositiveNumber(session?.id) ?? bookingSessionId;
  const sessionFilter = getSessionFilter(
    session?.date ?? getMetadataString(metadata, ['bookingDate', 'date'])
  );
  const bookingPath = bookingsManagementPath('view', {
    bookingId: String(bookingId),
    bookingTabSid: 'booking-sessions',
    sessionFilter,
  });

  return {
    bookingPath,
    sessionPath: sessionId
      ? `${bookingPath}?sessionID=${sessionId}`
      : undefined,
  };
};

const buildChips = (
  session: BookingSession | undefined,
  metadata: ChannelMetadata
): ContextChip[] => {
  const firstCleaner = session?.booking_session_cleaners?.[0];

  return [
    { label: 'Client', value: formatName(session?.user) },
    {
      label: 'Cleaner',
      value: firstCleaner ? formatName(firstCleaner) : 'Unassigned',
    },
    {
      label: 'Date',
      value: session?.date
        ? formatDateValue(session.date)
        : getMetadataString(metadata, ['bookingDate', 'date']),
    },
    {
      label: 'Time',
      value:
        session?.time || getMetadataString(metadata, ['bookingTime', 'time']),
    },
    { label: 'Address', value: formatAddressValue(session) },
    {
      label: 'Status',
      value: session?.booking_session_status?.display ?? 'N/A',
      tone: getStatusTone(session?.booking_session_status),
    },
    {
      label: 'Payment',
      value: session?.booking_session_payment_status?.display ?? 'N/A',
      tone: getStatusTone(session?.booking_session_payment_status),
    },
    { label: 'Access type', value: session?.access_type || 'N/A' },
  ];
};

export const useJobContextHeaderData = (
  channel: StreamChannel<StreamChatType>,
  enabled = true
): JobContextHeaderData => {
  const metadata = useMemo(() => getChannelMetadata(channel), [channel]);
  const bookingSessionId = useMemo(
    () => resolveBookingSessionId(channel),
    [channel]
  );
  const hasMetadataFallback = Boolean(
    getMetadataString(metadata, ['bookingDate', 'date']) !== 'N/A' ||
      getMetadataString(metadata, ['bookingTime', 'time']) !== 'N/A'
  );

  const {
    data: session,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['chatJobContext', bookingSessionId],
    queryFn: () => getBookingSession(bookingSessionId as number),
    enabled: Boolean(enabled && bookingSessionId),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const isLoading = Boolean(
    enabled && bookingSessionId && isFetching && !session
  );
  const canRenderContext = Boolean(
    enabled &&
      (bookingSessionId || hasMetadataFallback) &&
      !(isError && !hasMetadataFallback) &&
      (session || hasMetadataFallback)
  );

  return {
    bookingSessionId,
    chips: canRenderContext ? buildChips(session, metadata) : [],
    isLoading,
    links: canRenderContext
      ? buildJobContextLinks(session, metadata, bookingSessionId)
      : null,
    metadata,
    session,
    shouldRender: isLoading || canRenderContext,
  };
};

export const JobContextActions = ({
  links,
}: {
  links: JobContextLinks | null;
}) => {
  if (!links) return null;

  return (
    <span className="job-context-header__actions">
      <Link className="job-context-header__action" to={links.bookingPath}>
        Open Booking
      </Link>
      {links.sessionPath && (
        <Link
          className="job-context-header__action job-context-header__action--primary"
          to={links.sessionPath}
        >
          Open Session
        </Link>
      )}
    </span>
  );
};

export const JobContextHeader = ({ context }: JobContextHeaderProps) => {
  if (!context.shouldRender) return null;

  if (context.isLoading) {
    return (
      <div className="job-context-header job-context-header--loading">
        Loading job context...
      </div>
    );
  }

  return (
    <div className="job-context-header" aria-label="Job context">
      {context.chips.map((chip) => (
        <span
          className={`job-context-header__chip job-context-header__chip--${
            chip.tone ?? 'default'
          }`}
          key={chip.label}
          title={`${chip.label}: ${chip.value}`}
        >
          <span className="job-context-header__chip-label">{chip.label}</span>
          <span className="job-context-header__chip-value">{chip.value}</span>
        </span>
      ))}
    </div>
  );
};
